import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';

const AIChatContext = createContext(null);

export const useAIChat = () => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChat must be used within an AIChatProvider');
  }
  return context;
};

export const AIChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [context, setContext] = useState(null); // { type: 'project'|'campaign', id: number, name: string }
  const [error, setError] = useState(null);
  
  const abortControllerRef = useRef(null);

  // Load conversations on mount
  useEffect(() => {
    if (isOpen && conversations.length === 0) {
      loadConversations();
    }
  }, [isOpen]);

  // Keyboard shortcut: Ctrl+Shift+A
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const response = await api.get('/chat/conversations');
      // Backend returns 'items' array
      setConversations(response.data.items || response.data.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      // Don't show error for empty conversations, it's normal for new users
      if (err.response?.status !== 404) {
        setError('Failed to load conversations');
      }
    }
  }, []);

  const loadConversation = useCallback(async (conversationUuid) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/chat/conversations/${conversationUuid}`);
      setCurrentConversation(response.data);
      setMessages(response.data.messages || []);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createConversation = useCallback(async (title = null) => {
    try {
      const payload = {
        title: title || 'New Conversation',
        context: context ? {
          entity_type: context.type,
          entity_id: context.id,
          additional_context: context.name
        } : undefined
      };
      const response = await api.post('/chat/conversations', payload);
      const newConversation = response.data;
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversation(newConversation);
      setMessages([]);
      return newConversation;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      setError('Failed to create conversation');
      return null;
    }
  }, [context]);

  const deleteConversation = useCallback(async (conversationUuid) => {
    try {
      await api.delete(`/chat/conversations/${conversationUuid}`);
      setConversations(prev => prev.filter(c => c.uuid !== conversationUuid));
      if (currentConversation?.uuid === conversationUuid) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setError('Failed to delete conversation');
    }
  }, [currentConversation]);

  const sendMessage = useCallback(async (content, useStreaming = true) => {
    if (!content.trim()) return;

    let conversation = currentConversation;
    
    // Create new conversation if none exists
    if (!conversation) {
      conversation = await createConversation();
      if (!conversation) return;
    }

    // Use uuid for API calls (backend expects uuid, not id)
    const conversationUuid = conversation.uuid || conversation.id;

    // Add user message optimistically
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: content,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    if (useStreaming) {
      await sendStreamingMessage(conversationUuid, content);
    } else {
      await sendNonStreamingMessage(conversationUuid, content);
    }
  }, [currentConversation, createConversation]);

  const sendStreamingMessage = async (conversationUuid, content) => {
    setIsStreaming(true);
    setStreamingMessage('');
    
    try {
      abortControllerRef.current = new AbortController();
      
      const token = localStorage.getItem('token');
      const tenant = localStorage.getItem('tenant');
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      
      const response = await fetch(`${baseUrl}/chat/conversations/${conversationUuid}/messages/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Slug': tenant
        },
        body: JSON.stringify({
          content: content,
          context_type: context?.type,
          context_id: context?.id
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullMessage = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              // Stream complete
              break;
            }
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullMessage += parsed.content;
                setStreamingMessage(fullMessage);
              }
              if (parsed.function_call) {
                // Handle function call result display
                fullMessage += `\n\n*${parsed.function_call.name}* executed.`;
                setStreamingMessage(fullMessage);
              }
            } catch (e) {
              // Non-JSON line, might be content
              fullMessage += data;
              setStreamingMessage(fullMessage);
            }
          }
        }
      }

      // Add final assistant message
      if (fullMessage) {
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: fullMessage,
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
      
      // Refresh conversation list (title may have been updated)
      loadConversations();
      
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Streaming error:', err);
        setError('Failed to get response');
      }
    } finally {
      setIsStreaming(false);
      setStreamingMessage('');
      abortControllerRef.current = null;
    }
  };

  const sendNonStreamingMessage = async (conversationUuid, content) => {
    setIsLoading(true);
    
    try {
      const response = await api.post(`/chat/conversations/${conversationUuid}/messages`, {
        content: content,
        context_type: context?.type,
        context_id: context?.id
      });
      
      const assistantMessage = response.data.message || response.data;
      setMessages(prev => [...prev, assistantMessage]);
      loadConversations();
      
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen(prev => !prev), []);

  const setCurrentContext = useCallback((type, id, name) => {
    setContext({ type, id, name });
  }, []);

  const clearContext = useCallback(() => {
    setContext(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Task and Plan Generation
  const [generatedTasks, setGeneratedTasks] = useState([]);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateTasksFromConversation = useCallback(async () => {
    if (!currentConversation) return null;
    
    setIsGenerating(true);
    try {
      const response = await api.post(`/chat/conversations/${currentConversation.uuid}/generate-tasks`, {
        context: context ? { type: context.type, id: context.id } : {}
      });
      const tasksData = response.data;
      setGeneratedTasks(tasksData.tasks || []);
      return tasksData;
    } catch (err) {
      console.error('Failed to generate tasks:', err);
      setError('Failed to generate tasks');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [currentConversation, context]);

  const generatePlanFromConversation = useCallback(async (goal = null) => {
    if (!currentConversation) return null;
    
    setIsGenerating(true);
    try {
      const response = await api.post(`/chat/conversations/${currentConversation.uuid}/generate-plan`, {
        goal,
        context: context ? { type: context.type, id: context.id } : {}
      });
      const planData = response.data.plan;
      setGeneratedPlan(planData);
      return planData;
    } catch (err) {
      console.error('Failed to generate plan:', err);
      setError('Failed to generate plan');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [currentConversation, context]);

  const saveTasks = useCallback(async (tasks) => {
    if (!currentConversation) return null;
    
    try {
      const response = await api.post(`/chat/conversations/${currentConversation.uuid}/save-tasks`, {
        tasks
      });
      setGeneratedTasks([]);
      return response.data;
    } catch (err) {
      console.error('Failed to save tasks:', err);
      setError('Failed to save tasks');
      return null;
    }
  }, [currentConversation]);

  const savePlan = useCallback(async (plan) => {
    if (!currentConversation) return null;
    
    try {
      const response = await api.post(`/chat/conversations/${currentConversation.uuid}/save-plan`, {
        plan
      });
      setGeneratedPlan(null);
      return response.data;
    } catch (err) {
      console.error('Failed to save plan:', err);
      setError('Failed to save plan');
      return null;
    }
  }, [currentConversation]);

  const clearGeneratedContent = useCallback(() => {
    setGeneratedTasks([]);
    setGeneratedPlan(null);
  }, []);

  const value = {
    // Panel state
    isOpen,
    openPanel,
    closePanel,
    togglePanel,
    
    // Conversations
    conversations,
    currentConversation,
    loadConversations,
    loadConversation,
    createConversation,
    deleteConversation,
    setCurrentConversation,
    
    // Messages
    messages,
    sendMessage,
    isLoading,
    isStreaming,
    streamingMessage,
    stopStreaming,
    
    // Context
    context,
    setCurrentContext,
    clearContext,
    
    // Task & Plan Generation
    generatedTasks,
    generatedPlan,
    isGenerating,
    generateTasksFromConversation,
    generatePlanFromConversation,
    saveTasks,
    savePlan,
    clearGeneratedContent,
    
    // Error
    error,
    clearError
  };

  return (
    <AIChatContext.Provider value={value}>
      {children}
    </AIChatContext.Provider>
  );
};

export default AIChatContext;
